/**
 * source-mode-lib.ts — Phần THUẦN của luồng "chọn nguồn AI (một cho cả tổ chức) rồi chọn model của tổ chức" (Nguồn AI và lượt AI):
 *  - nhận diện tổ chức đang dùng MỘT nguồn cho mọi nền tảng hay KẾT HỢP (dữ liệu cũ: nền tảng khác nguồn nhau);
 *  - dựng body `PUT /ai/sources` để thống nhất một nguồn cho mọi nền tảng;
 *  - model tổ chức đang dùng trong nguồn đang xem; "Dùng model này" có phải chuyển nguồn trước không;
 *  - nhãn trạng thái key của một nền tảng (Key của tôi), nhãn hạng model.
 */
import { i18n } from '@/lib/i18n'
import { byokBroken, SOURCE_LABEL, type AiProviderSource, type AiSource, type BadgeTone, type ByokState } from '@/lib/ai-credits'

export type SourceMode = AiSource | 'mixed' | 'none'

type SrcLite = Pick<AiProviderSource, 'provider' | 'name' | 'source'>

/** Nguồn tổ chức đang dùng: một nguồn cho mọi nền tảng / kết hợp / chưa có dữ liệu. */
export function orgSourceMode(providers: SrcLite[] | null | undefined): SourceMode {
  const set = new Set((providers ?? []).map((p) => p.source))
  if (set.size === 0) return 'none'
  if (set.size > 1) return 'mixed'
  return [...set][0]
}

/** "ChatGPT dùng Key của tôi, Claude dùng AI của eCDP, …" (chỉ ý nghĩa khi kết hợp). */
export function mixedSummary(providers: SrcLite[] | null | undefined): string {
  return (providers ?? []).map((p) => i18n.t('aiOps:sourceModeLib.mixedItem', { name: p.name, source: SOURCE_LABEL[p.source] })).join(', ')
}

/** Body `PUT /ai/sources` dùng MỘT nguồn cho mọi nền tảng (nền tảng lấy từ `GET /ai/sources`). */
export function unifySourcesBody(providers: Array<Pick<AiProviderSource, 'provider'>>, source: AiSource): { defaultSource: AiSource; providers: Record<string, AiSource> } {
  return { defaultSource: source, providers: Object.fromEntries(providers.map((p) => [p.provider, source])) }
}

/** Nguồn đang XEM ở bước 2: người dùng vừa chọn → theo đó; chưa chọn → nguồn tổ chức (kết hợp / chưa có → chưa xem gì). */
export function viewedSource(picked: AiSource | null, mode: SourceMode): AiSource | null {
  if (picked) return picked
  return mode === 'platform' || mode === 'byok' ? mode : null
}

/**
 * Model tổ chức đang dùng NẾU nó chạy bằng nguồn đang xem (thẻ "Đang dùng" ở bước 2): xem AI của eCDP mà tổ chức dùng Key
 * của tôi → không thẻ nào "Đang dùng" (bấm "Dùng model này" sẽ chuyển nguồn).
 */
export function orgModelInView(
  config: { provider?: string | null; model?: string | null } | null | undefined,
  providers: Array<Pick<AiProviderSource, 'provider' | 'source'>> | null | undefined,
  view: AiSource | null,
): { provider: string; model: string } | null {
  if (!config?.provider || !config.model || !view) return null
  const src = providers?.find((p) => p.provider === config.provider)?.source
  return src === view ? { provider: config.provider, model: config.model } : null
}

/** "Dùng model này" phải chuyển cả tổ chức sang nguồn đang xem trước (tổ chức đang dùng nguồn khác / kết hợp). */
export const needsSourceSwitch = (mode: SourceMode, view: AiSource | null): boolean => !!view && mode !== view

/** Trạng thái key của một nền tảng: "Chưa có key" / "Đang hoạt động · …abcd" / "Key lỗi". */
export function keyStatus(byok: ByokState | null | undefined): { label: string; tone: BadgeTone; error: string | null } {
  if (!byok?.configured) return { label: i18n.t('aiOps:sourceModeLib.keyNone'), tone: 'outline', error: null }
  if (byokBroken(byok)) return { label: i18n.t('aiOps:sourceModeLib.keyBroken'), tone: 'destructive', error: byok.lastError ?? null }
  return { label: byok.hint ? i18n.t('aiOps:sourceModeLib.keyActiveHint', { hint: byok.hint }) : i18n.t('aiOps:sourceModeLib.keyActive'), tone: 'success', error: null }
}

// Hạng model + khoá theo gói: dùng chung với lưới thẻ model.
export { TIER_LABEL, tierLabel, lockedByPlan } from '@/components/ai/model-card-grid-lib'
